import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Sessions live in HttpOnly cookies only; purge any token left behind by older builds.
try { localStorage.removeItem("mfg_token"); } catch { /* private mode */ }

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Any binary download (PDF / CSV / ZIP) shows the spinning-shield overlay while it renders.
const pdfBusy = (delta) => window.dispatchEvent(new CustomEvent("mfg:pdf-busy", { detail: delta }));
export async function trackPdf(promise) {
  pdfBusy(1);
  try { return await promise; } finally { pdfBusy(-1); }
}
// Authenticated download (cookie session) → saves the file with the server's filename or `fallback`.
export async function downloadFile(path, { params, fallback = "download.pdf" } = {}) {
  const res = await api.get(path, { params, responseType: "blob", timeout: 120000 });
  const cd = res.headers?.["content-disposition"] || "";
  const m = /filename="?([^";]+)"?/.exec(cd);
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = m ? m[1] : fallback; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
api.interceptors.request.use((config) => {
  if (config.responseType === "blob" && !config._retried) { config._pdfTracked = true; pdfBusy(1); }
  return config;
});
api.interceptors.response.use(
  (res) => { if (res.config?._pdfTracked) pdfBusy(-1); return res; },
  (error) => { if (error.config?._pdfTracked) pdfBusy(-1); return Promise.reject(error); },
);

const NO_REFRESH = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout", "/auth/google/session"];
let refreshing = null;

// Cookies are HttpOnly, so remember client-side that a session was minted. Lets us refresh
// silently when the 8-hour access cookie has expired but the multi-day refresh cookie hasn't.
const SESSION_FLAG = "mfg_session";
export const markSession = (on) => { try { on ? localStorage.setItem(SESSION_FLAG, "1") : localStorage.removeItem(SESSION_FLAG); } catch { /* private mode */ } };
const hasSession = () => { try { return localStorage.getItem(SESSION_FLAG) === "1"; } catch { return false; } };

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { config, response } = error;
    const url = config?.url || "";
    // "Not authenticated" with no remembered session = anonymous visitor — nothing to refresh.
    const noSession = response?.data?.detail === "Not authenticated" && !hasSession();
    if (response?.status !== 401 || config?._retried || noSession || NO_REFRESH.some((p) => url.includes(p))) {
      return Promise.reject(error);
    }
    try {
      refreshing = refreshing || api.post("/auth/refresh").finally(() => { refreshing = null; });
      const { data } = await refreshing;
      markSession(true);
      window.dispatchEvent(new CustomEvent("mfg:session-refreshed", { detail: data }));
    } catch {
      markSession(false);
      window.dispatchEvent(new CustomEvent("mfg:session-lost"));
      return Promise.reject(error);
    }
    return api({ ...config, _retried: true });
  },
);

export function formatApiErrorDetail(detail, fallback) {
  // Accepts a raw FastAPI `detail` OR a thrown Axios error (unwraps response.data.detail)
  if (detail && typeof detail === "object" && (detail.isAxiosError || detail.response || detail.config)) {
    const d = detail.response?.data?.detail ?? detail.response?.data?.message;
    if (d == null) {
      if (!detail.response) return fallback || "Network error — check your connection and try again.";
      return fallback || `Request failed (${detail.response.status})`;
    }
    detail = d;
  }
  if (detail == null) return fallback || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ") || fallback || "Something went wrong.";
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail instanceof Error) return fallback || detail.message;
  return String(detail);
}

export default api;
